import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { faChevronDown, faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { CopywritingFailure } from "../utils/generateBarLead.util";

/**
 * Collapsed "Generate messages with AI" strip — a slim pill that expands the
 * floating "Tell AI" panel on click. Extracted from {@link useAddInboxChat}
 * so that large hook stays under the file-length gate. Pinned above the draft
 * editor whenever the full inline GenerateBar is hidden (the chat already has
 * conversations and/or drafts, PREC-3697) or the chat was opened to write
 * manually — the AI entry point stays reachable instead of disappearing.
 */
export const GenerateBarCollapsedStrip: FunctionComponent<{
	onOpen: () => void;
	/** Overrides the width/alignment classes (default: draft-strip placement). */
	className?: string;
	/**
	 * The lead's last generation failed. Its error is shown above the drafts the failure left in
	 * place; the panel this strip opens offers "Try again" (PREC-4052).
	 */
	failure?: CopywritingFailure | null;
}> = ({ onOpen, className = "max-w-[80%] self-end", failure = null }) => {
	const { t } = useTranslation();

	const strip = (
		<button
			className={classNames(
				"w-full cursor-pointer flex rounded-xl border bg-white border-border-light px-4 py-2.5 items-center gap-3 hover:bg-gray-50 transition-colors text-left",
				className
			)}
			data-testid="generate-bar-collapsed-strip"
			type="button"
			// Prevent the button from receiving focus on click — without this the
			// browser auto-scrolls the focused button into view inside the chat's
			// overflow-y-auto container, jumping the chat scroll position.
			onMouseDown={(e) => e.preventDefault()}
			onClick={onOpen}
		>
			<Icon className="w-3.5 h-3.5 text-blue-action shrink-0" icon={faSparkles} />
			<span className="text-sm text-text truncate flex-1">
				{t("messages.generateBar.collapsedLabel")}
			</span>
			<Icon className="w-3 h-3 text-text-meta shrink-0" icon={faChevronDown} />
		</button>
	);

	if (!failure) {
		return strip;
	}

	return (
		<div className="flex w-full flex-col gap-1">
			{/* The colour sits on each <p>: index.css gives every paragraph its own text colour. */}
			<div className="text-xs" data-testid="generate-bar-failure" role="alert">
				{failure.isCredits ? (
					<p className="text-status-error">{t("messages.generateBar.status.notEnoughCredits")}</p>
				) : (
					<>
						<p className="text-status-error">
							{t("messages.generateBar.status.failedAbove", { error: failure.message })}
						</p>
						<p className="text-text-meta">{t("messages.generateBar.status.failedAboveHint")}</p>
					</>
				)}
			</div>
			{strip}
		</div>
	);
};
